import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { usePageTitle } from '../../../../lib/hooks';
import { PageHeader } from '../../../../components/layout/PageHeader';
import { Button } from '../../../../components/ui/Button.js';
import { Input } from '../../../../components/ui/FormField.js';
import { StatusBadge } from '../../../../components/ui/StatusBadge.js';
import { EntityActionMenu } from '../../../../components/ui/EntityActionMenu.js';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../../components/ui/Dialog.js';
import { useToast } from '../../../../components/ui/Toast.js';
import { Plus, Search, Tablet, ShieldCheck, RotateCcw, Clock } from 'lucide-react';

import { useDevices, useDeviceLogs, useReturnDevice } from '../../../../features/settings/network/devices.api';

export const Route = createFileRoute('/_auth/settings/devices/')({
  component: DevicesSettingsPage,
});

interface TabletDevice {
  id: string;
  fingerprint: string;
  label: string;
  assignedAgent: string;
  status: 'ISSUED' | 'AVAILABLE' | 'MAINTENANCE';
  registeredAt: string;
  lastSeenAt: string;
}

interface IssueLog {
  id: string;
  deviceName: string;
  agentName: string;
  issuedAt: string;
  returnedAt: string | null;
  remarks: string;
}

function DevicesSettingsPage() {
  usePageTitle('Devices Settings');
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = React.useState<'devices' | 'logs'>('devices');
  const { data: devicesData = [] } = useDevices();
  const devices = devicesData as TabletDevice[];
  const { data: logsData = [] } = useDeviceLogs();
  const logs = logsData as IssueLog[];

  // Search states
  const [deviceQuery, setDeviceQuery] = React.useState('');
  const [logQuery, setLogQuery] = React.useState('');

  // Modals
  const [isReturnOpen, setIsReturnOpen] = React.useState(false);
  const [selectedDevice, setSelectedDevice] = React.useState<TabletDevice | null>(null);

  const returnDeviceMutation = useReturnDevice();

  const handleReturnDevice = () => {
    if (!selectedDevice) return;

    returnDeviceMutation.mutate(selectedDevice.id, {
      onSuccess: () => {
        setIsReturnOpen(false);
        setSelectedDevice(null);
        toast({
          type: 'success',
          title: 'Device Returned',
          description: 'The physical device has been de-registered and returned to safe inventory.',
        });
      },
      onError: (err: unknown) => {
        toast({
          type: 'error',
          title: 'Error',
          description: err instanceof Error ? err.message : 'Failed to return device.',
        });
      }
    });
  };

  const filteredDevices = devices.filter(d =>
    d.label.toLowerCase().includes(deviceQuery.toLowerCase()) ||
    d.fingerprint.toLowerCase().includes(deviceQuery.toLowerCase()) ||
    d.assignedAgent.toLowerCase().includes(deviceQuery.toLowerCase())
  );

  const filteredLogs = logs.filter(l =>
    l.deviceName.toLowerCase().includes(logQuery.toLowerCase()) ||
    l.agentName.toLowerCase().includes(logQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Secured Field Devices"
        subtitle="Authorize agent tablets, manage offline security lock-downs, and view daily device dispatch logs."
        action={
          <Button variant="primary" onClick={() => navigate({ to: '/settings/devices/new' })}>
            <Plus className="w-4 h-4 mr-2" />
            Register New Tablet
          </Button>
        }
      />

      {/* Navigation Tabs */}
      <div className="flex gap-4 border-b border-border">
        <Button
          variant="ghost"
          onClick={() => setActiveTab('devices')}
          className={`pb-2 px-1 text-sm font-semibold transition-colors border-b-2 ${
            activeTab === 'devices' ? 'border-accent-500 text-text-primary' : 'border-transparent text-text-muted hover:text-text-primary'
          }`}
        >
          Device Registry
        </Button>
        <Button
          variant="ghost"
          onClick={() => setActiveTab('logs')}
          className={`pb-2 px-1 text-sm font-semibold transition-colors border-b-2 ${
            activeTab === 'logs' ? 'border-accent-500 text-text-primary' : 'border-transparent text-text-muted hover:text-text-primary'
          }`}
        >
          Dispatch & Return History
        </Button>
      </div>

      {/* Registry Tab */}
      {activeTab === 'devices' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div className="relative max-w-md flex-1">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-text-muted" />
              <Input
                type="text"
                placeholder="Search device label, IMEI / fingerprint, assigned agent..."
                value={deviceQuery}
                onChange={e => setDeviceQuery(e.target.value)}
                className="w-full h-9 pl-9 pr-3 bg-surface-1 border border-border rounded-[6px] text-sm text-text-primary focus:outline-none focus:border-accent-500"
              />
            </div>
          </div>

          <div className="bg-surface-1 border border-border rounded-xl overflow-hidden shadow-sm">
            <table className="w-full border-collapse text-left text-sm">
              <thead className="bg-surface-2 text-text-secondary font-semibold uppercase tracking-wider text-xs border-b border-border">
                <tr>
                  <th className="p-4">Device Info</th>
                  <th className="p-4">Secure Fingerprint</th>
                  <th className="p-4">Currently Issued To</th>
                  <th className="p-4">Status</th>
                  <th className="p-4">Registered Date</th>
                  <th className="p-4">Last Connection</th>
                  <th className="p-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border text-text-primary">
                {filteredDevices.map(d => (
                  <tr key={d.id} className="hover:bg-surface-2 transition-colors">
                    <td className="p-4">
                      <div className="flex items-center gap-2.5">
                        <Tablet className="w-5 h-5 text-accent-500 shrink-0" />
                        <span className="font-semibold">{d.label}</span>
                      </div>
                    </td>
                    <td className="p-4 font-mono text-xs">{d.fingerprint}</td>
                    <td className="p-4">
                      {d.assignedAgent === 'None' ? (
                        <span className="text-text-muted text-xs italic">Unassigned</span>
                      ) : (
                        <span className="font-medium">{d.assignedAgent}</span>
                      )}
                    </td>
                    <td className="p-4">
                      <StatusBadge
                        status={d.status === 'ISSUED' ? 'warning' : d.status === 'MAINTENANCE' ? 'danger' : 'success'}
                        label={d.status}
                      />
                    </td>
                    <td className="p-4 font-mono text-xs">{d.registeredAt}</td>
                    <td className="p-4 font-mono text-xs">
                      {new Date(d.lastSeenAt).toLocaleString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                    <td className="p-4 text-right">
                      <EntityActionMenu
                        actions={[
                          {
                            label: 'Issue Tab',
                            icon: <ShieldCheck className="w-4 h-4" />,
                            hidden: d.status !== 'AVAILABLE',
                            onClick: () => {
                              navigate({ to: `/settings/devices/$id/dispatch`, params: { id: d.id } });
                            },
                          },
                          {
                            label: 'Return Tab',
                            icon: <RotateCcw className="w-4 h-4" />,
                            hidden: d.status !== 'ISSUED',
                            onClick: () => {
                              setSelectedDevice(d);
                              setIsReturnOpen(true);
                            },
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Dispatch History Tab */}
      {activeTab === 'logs' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div className="relative max-w-md flex-1">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-text-muted" />
              <Input
                type="text"
                placeholder="Search log agent, tablet name..."
                value={logQuery}
                onChange={e => setLogQuery(e.target.value)}
                className="w-full h-9 pl-9 pr-3 bg-surface-1 border border-border rounded-[6px] text-sm text-text-primary focus:outline-none focus:border-accent-500"
              />
            </div>
          </div>

          <div className="bg-surface-1 border border-border rounded-xl overflow-hidden shadow-sm">
            <table className="w-full border-collapse text-left text-sm">
              <thead className="bg-surface-2 text-text-secondary font-semibold uppercase tracking-wider text-xs border-b border-border">
                <tr>
                  <th className="p-4">Device Model</th>
                  <th className="p-4">Assigned Agent</th>
                  <th className="p-4">Issued At</th>
                  <th className="p-4">Returned At</th>
                  <th className="p-4">Dispatch Remarks</th>
                  <th className="p-4">Device State</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border text-text-primary">
                {filteredLogs.map(l => (
                  <tr key={l.id} className="hover:bg-surface-2 transition-colors">
                    <td className="p-4 font-semibold">{l.deviceName}</td>
                    <td className="p-4 font-medium">{l.agentName}</td>
                    <td className="p-4 font-mono text-xs">
                      {new Date(l.issuedAt).toLocaleString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                    <td className="p-4 font-mono text-xs text-text-secondary">
                      {l.returnedAt ? (
                        new Date(l.returnedAt).toLocaleString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      ) : (
                        <span className="text-warning-500 font-semibold flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5 animate-pulse" />
                          Out on Field
                        </span>
                      )}
                    </td>
                    <td className="p-4 text-xs text-text-secondary max-w-xs truncate">{l.remarks}</td>
                    <td className="p-4">
                      {l.returnedAt ? (
                        <StatusBadge status="success" label="Returned" />
                      ) : (
                        <StatusBadge status="warning" label="Issued" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Return Device Dialog */}
      <Dialog open={isReturnOpen} onOpenChange={setIsReturnOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Device Return</DialogTitle>
            <DialogDescription>
              De-register this active agent device dispatch and return it to company inventory.
            </DialogDescription>
          </DialogHeader>

          <div className="bg-surface-2 p-3 rounded-lg border border-border flex items-center gap-2">
            <Tablet className="w-5 h-5 text-accent-500 shrink-0" />
            <div>
              <p className="text-xs font-bold text-text-primary">{selectedDevice?.label}</p>
              <p className="text-caption font-mono text-text-muted mt-0.5">Assigned: {selectedDevice?.assignedAgent}</p>
            </div>
          </div>

          <DialogFooter className="pt-4">
            <Button type="button" variant="secondary" onClick={() => {
              setIsReturnOpen(false);
              setSelectedDevice(null);
            }}>
              Cancel
            </Button>
            <Button type="button" variant="primary" onClick={handleReturnDevice}>
              De-register Return
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
